/**
 * Ensures location permission is granted for outdoor runs.
 * On iOS: requires BOTH (1) Capacitor location granted AND (2) native "Always" for background tracking.
 * On Android: standard location permission is sufficient.
 */
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import { registerPlugin } from "@capacitor/core";

export type LocationPermissionReason = "denied" | "whenInUse" | "notGranted";

export type EnsureLocationResult =
  | { ok: true }
  | { ok: false; reason: LocationPermissionReason };

type LocationStatus = "always" | "whenInUse" | "denied" | "notDetermined";

interface StraivLocationPlugin {
  getLocationPermissionStatus?: () => Promise<{ status?: string }>;
  openSettings?: () => Promise<void>;
}

interface BGPlugin {
  addWatcher?: (opts: unknown, cb: () => void) => Promise<string>;
  removeWatcher?: (opts: { id: string }) => Promise<void>;
  openSettings?: () => Promise<void>;
}

let _straivLocation: StraivLocationPlugin | null = null;
let _bgGeo: BGPlugin | null = null;

const getStraivLocation = (): StraivLocationPlugin | null => {
  if (_straivLocation) return _straivLocation;
  try {
    _straivLocation = registerPlugin("StraivLocation") as StraivLocationPlugin;
    return _straivLocation;
  } catch {
    return null;
  }
};

const getBackgroundGeolocation = (): BGPlugin | null => {
  if (_bgGeo) return _bgGeo;
  for (const name of ["BackgroundGeolocation", "CapacitorCommunityBackgroundGeolocation"]) {
    try {
      const p = registerPlugin(name) as { addWatcher?: unknown } | null;
      if (p && typeof p.addWatcher === "function") {
        _bgGeo = p as BGPlugin;
        return _bgGeo;
      }
    } catch {}
  }
  return null;
};

/** Open app Settings. Call from user gesture (e.g. button click). */
export async function openLocationSettings(): Promise<void> {
  const StraivLocation = getStraivLocation();
  const BG = getBackgroundGeolocation();
  try {
    if (StraivLocation?.openSettings) {
      await StraivLocation.openSettings();
      return;
    }
    if (BG?.openSettings) {
      await BG.openSettings();
      return;
    }
  } catch {}
  if (Capacitor.getPlatform() === "ios") {
    window.open("app-settings:", "_self");
  }
}

/**
 * Ensures location is ready for outdoor run. On iOS, requires "Always" for background tracking.
 */
export async function ensureLocationForOutdoorRun(): Promise<EnsureLocationResult> {
  if (Capacitor.getPlatform() !== "ios") {
    try {
      const p = await Geolocation.checkPermissions();
      return p.location === "granted" ? { ok: true } : { ok: false, reason: "notGranted" };
    } catch {
      return { ok: false, reason: "notGranted" };
    }
  }

  const checkStatus = async (): Promise<LocationStatus> => {
    try {
      const StraivLocation = getStraivLocation();
      if (StraivLocation?.getLocationPermissionStatus) {
        const result = await StraivLocation.getLocationPermissionStatus();
        const raw = result?.status ?? (result as { value?: { status?: string } })?.value?.status ?? null;
        return (raw ?? "notDetermined") as LocationStatus;
      }
    } catch {}
    const p = await Geolocation.checkPermissions();
    if (p.location !== "granted") return "denied";
    return "whenInUse";
  };

  try {
    let status = await checkStatus();

    if (status === "always") return { ok: true };

    if (status === "denied") {
      await new Promise((r) => setTimeout(r, 600));
      const sDenied = await checkStatus();
      if (sDenied === "always") return { ok: true };
      if (sDenied !== "whenInUse") return { ok: false, reason: "denied" };
      status = sDenied;
    }

    if (status === "notDetermined") {
      const req = await Geolocation.requestPermissions();
      if (req.location !== "granted") {
        return { ok: false, reason: "denied" };
      }
      status = await checkStatus();
      if (status === "always") return { ok: true };
      if (status === "denied") return { ok: false, reason: "denied" };
    }

    if (status === "whenInUse") {
      // Brief re-check (user may have just returned from Settings)
      await new Promise((r) => setTimeout(r, 150));
      const s2 = await checkStatus();
      if (s2 === "always") return { ok: true };

      // Show dialog immediately - addWatcher can be slow and block for many seconds
      return { ok: false, reason: "whenInUse" };
    }

    const p = await Geolocation.checkPermissions();
    return p.location === "granted" ? { ok: true } : { ok: false, reason: "notGranted" };
  } catch (err) {
    console.warn("[runLocationPermission] Error:", err);
    await new Promise((r) => setTimeout(r, 600));
    try {
      const StraivLocation = getStraivLocation();
      if (StraivLocation?.getLocationPermissionStatus) {
        const result = await StraivLocation.getLocationPermissionStatus();
        const raw = result?.status ?? (result as { value?: { status?: string } })?.value?.status ?? null;
        const status = (raw ?? "notDetermined") as LocationStatus;
        if (status === "always") return { ok: true };
        if (status === "denied") return { ok: false, reason: "denied" };
        if (status === "whenInUse") return { ok: false, reason: "whenInUse" };
      }
    } catch {}
    if (Capacitor.getPlatform() === "ios") {
      return { ok: false, reason: "whenInUse" };
    }
    try {
      const perm = await Geolocation.checkPermissions();
      return perm.location === "granted" ? { ok: true } : { ok: false, reason: "notGranted" };
    } catch {
      return { ok: false, reason: "notGranted" };
    }
  }
}
